import React, { type ReactNode, useCallback, useRef, useState, useMemo } from 'react';

import { fg } from '@atlaskit/platform-feature-flags';
// eslint-disable-next-line @atlaskit/design-system/no-emotion-primitives -- to be migrated to @atlaskit/primitives/compiled – go/akcss
import { Box, Pressable, xcss } from '@atlaskit/primitives';
// eslint-disable-next-line @atlaskit/design-system/no-emotion-primitives -- TODO: migrate to @atlaskit/primitives/compiled
import { Show } from '@atlaskit/primitives/responsive';
import { token } from '@atlaskit/tokens';
import { enrichResultTitle } from '@atlassian/search-common/enriched-title';
import { useKeyboardNavigation } from '@atlassian/search-dialog/search-keyboard';

import { MATCHING_WORK_ITEM_ID } from '../../../constants/intent-router';
import { type PrimaryProductKey } from '../../../constants/product-keys';
import type { ConnectorSource } from '../../../constants/schemas/3p-config';
import { isAuxClick } from '../../../utils/clicks';
import {
	type ResultType,
	SearchDialogResultAnalyticsWrapper,
} from '../../../utils/quick-find/analytics';
import { useCrossAppLinkHandlers } from '../../../utils/use-cross-app-link-handlers';
import { useSearchCrossProductUrlWrapper } from '../../../utils/use-cross-product-url-wrapper';
import { Link } from '../../link-component';
import { SearchMetadata, type SearchMetadataPart } from '../../search-metadata';
import { useItemsAnalyticsContext } from '../items-analytics-provider';

const containerStyles = xcss({
	marginInlineStart: 'space.100',
	marginInlineEnd: 'space.100',
});

const resultStyles = xcss({
	paddingBlockStart: 'space.075',
	paddingBlockEnd: 'space.075',
	paddingInlineStart: 'space.100',
	paddingInlineEnd: 'space.100',
	borderRadius: 'radius.large',
	display: 'flex',
	alignItems: 'center',
	cursor: 'pointer',
});

const resultHoveredStlyes = xcss({
	backgroundColor: 'color.background.neutral.subtle.hovered',
});

const keyboardHighlightedStyles = xcss({
	backgroundColor: 'color.background.neutral.subtle.hovered',
});

const iconAreaStyles = xcss({
	width: 'size.200',
	height: 'size.200',
	display: 'flex',
	alignItems: 'center',
	justifyContent: 'center',
});

const middleAreaStyles = xcss({
	display: 'flex',
	flexDirection: 'column',
	minWidth: '0',
	flex: 1,
	marginLeft: 'space.150',
});

const titleStyles = xcss({
	overflow: 'clip',
	whiteSpace: 'nowrap',
	textOverflow: 'ellipsis',
	font: 'font.body',
	// eslint-disable-next-line @atlaskit/design-system/use-tokens-typography
	lineHeight: '16px',
});

const metadataStyles = xcss({
	color: 'color.text.subtlest',
	marginTop: 'space.050',
	font: 'font.body.small',
	// eslint-disable-next-line @atlaskit/design-system/use-tokens-typography
	lineHeight: '13px',
	display: 'flex',
	alignItems: 'center',
	minWidth: '0',
});

const textOverflowStyles = xcss({
	overflow: 'hidden',
	whiteSpace: 'nowrap',
	textOverflow: 'ellipsis',
});

const rightSectionStyles = xcss({
	color: 'color.text.subtlest',
	font: 'font.body.small',
	display: 'flex',
	alignItems: 'center',
	justifyContent: 'flex-end',
	marginLeft: 'space.150',
});

const linkStyles = xcss({
	color: 'color.text',
	textDecoration: 'none',
	display: 'inline-block',
	width: '100%',
	borderRadius: 'radius.large',
	':hover': {
		textDecoration: 'none',
		color: 'color.text',
	},
	':visited': {
		textDecoration: 'none',
		color: 'color.text',
	},
	':focus': {
		textDecoration: 'none',
		color: 'color.text',
	},
});

const headingStyle = xcss({
	display: 'inline-flex',
	gap: 'space.075',
	alignItems: 'center',
	maxWidth: '100%',
	// Fix height to match titleStyles lineHeight so that inline
	// children with built-in margins (e.g. Tag) do not cause the row to grow taller.
	height: token('space.200'),
});

type SearchResultProps = {
	id?: string;
	icon: ReactNode;
	metadata?: SearchMetadataPart[];
	title: ReactNode;
	resultType: ResultType;
	product?: string;
	primaryProduct?: PrimaryProductKey;
	primarySubProduct?: string;
	url?: string;
	rightMetadata?: ReactNode;
	highlightedRightMetadata?: ReactNode;
	hoveredRightMetadata?: ReactNode;
	isThirdParty?: boolean;
	cloudId?: string;
	highlightedTitle?: string;
	includeInAnalytics?: boolean;
	titleMetadataParts?: SearchMetadataPart[];
	minutesSinceActivityTimestamp?: number | null;
	recentActivityEventType?: string | null;
	objectType?: string;
	connectorType?: ConnectorSource;
	zeroQueryType?: string;

	onResultClicked?: (
		e: React.MouseEvent<HTMLDivElement> | React.MouseEvent<HTMLButtonElement>,
		data: SearchResultEventArguments,
	) => void;
	onResultContextMenu?: (
		e: React.MouseEvent<HTMLDivElement> | React.MouseEvent<HTMLButtonElement>,
		data: SearchResultEventArguments,
	) => void;
	onResultHighlighted?: (data: SearchResultEventArguments) => void;
};

export type SearchResultEventArguments = {
	sectionCount?: number;
	sectionIndex?: number;
	resultCount?: number;
	globalIndex?: number;
	indexWithinSection?: number;
	activityTimestamp?: string;
	oldIndexData?: SearchResultEventArguments;
	newIndexData?: SearchResultEventArguments;
	isAuxClick?: boolean;
	resultType?: ResultType;
};

export const SearchResult = ({
	icon,
	title,
	resultType,
	product,
	primaryProduct,
	primarySubProduct,
	metadata = [],
	rightMetadata,
	hoveredRightMetadata,
	highlightedRightMetadata,
	url,
	isThirdParty = false,
	highlightedTitle,
	includeInAnalytics = true,
	onResultClicked,
	onResultContextMenu,
	onResultHighlighted,
	titleMetadataParts,
	id,
	minutesSinceActivityTimestamp,
	recentActivityEventType,
	objectType,
	connectorType,
	zeroQueryType,
}: SearchResultProps): React.JSX.Element => {
	const wrapperRef = useRef<HTMLDivElement>(null);
	const ref = useRef<HTMLDivElement>();

	const [isHovered, setIsHovered] = useState(false);

	const { getItemIndexes } = useItemsAnalyticsContext();

	const navigationTrackingId = useMemo(() => {
		if (resultType === 'intentRouterWorkItem') {
			return `${resultType}:${MATCHING_WORK_ITEM_ID}`;
		}
		return id ? `${resultType}:${id}` : null;
	}, [id, resultType]);

	const wrapCrossProductUrl = useSearchCrossProductUrlWrapper({
		bridge: 'quickFind',
		primaryProduct,
		primarySubProduct,
	});
	const wrappedUrl = wrapCrossProductUrl(url, product);
	// Reports the cross-app interaction using the `xpis` param already on
	// `wrappedUrl`. A no-op when the URL was not wrapped.
	const crossAppLinkHandlers = useCrossAppLinkHandlers({
		href: wrappedUrl,
		destinationApp: product,
	});

	const [isKeyboardHighlighted, setRef] = useKeyboardNavigation<HTMLDivElement>({
		onKeydownCallback: (e: KeyboardEvent) => {
			/**
			 * Two different event listeners are used to handle enter key presses
			 * when the input is focused:
			 * - the onKeyDown callback on the Textfield component which uses
			 *   React's synthetic event listeners (onKeyDown)
			 * - the keyboard highlight provider's onKeydownCallback which
			 *   uses native event listeners (window.addEventListener). This one
			 *   will only be called if a search result is highlighted.
			 * Ideally there should be only one event listener however, it isn't
			 * worth the effort to refactor the keyboard highlight provider right now.
			 *
			 * Given that the keyboard highlight provider's callback uses native
			 * event listeners, it will always be called first. This is a problem
			 * because there is no way for the keyboard highlight provider to
			 * communicate that the event has been handled, resulting in a bug
			 * where we try to handle the event twice if a search result was
			 * highlighted. It must be noted that it cannot be guaranteed that
			 * the search results are still highlighted after the native event
			 * listener has executed as it could close the search dialog.
			 *
			 * To fix this, a somewhat hacky solution has been added to delay
			 * the click event in the keyboard highlight provdier by one frame
			 * in search-dialog-result/index. This allows the React synthetic
			 * event to decide if it should handle the event by checking if a
			 * search result is highlighted, otherwise it will do nothing and
			 * let the keyboard highlight provider handle the event.
			 */
			requestAnimationFrame(() => {
				if (e.key === 'Enter') {
					ref.current?.click();
				}
			});
		},
		// Used by KeyboardHighlightProvider to keep highlighting stable across re-orders.
		// Also enables imperative highlighting by a stable tracking id.
		//
		// NOTE: `id` alone can collide across sections (e.g. suggestion + recent query),
		// so we namespace it by `resultType`.
		// we need a fixed tracking ID for work item in intent detection to highlight it automatically
		navigationTrackingId,
		onHighlightedCallback: () =>
			onResultHighlighted?.({
				...(getItemIndexes(id) ?? {}),
				resultType,
			}),
	});

	const onClick = useCallback(
		(
			event: React.MouseEvent<HTMLDivElement> | React.MouseEvent<HTMLButtonElement>,
			{
				isAuxClick = false,
			}: {
				isAuxClick?: boolean;
			} = {},
		) =>
			onResultClicked?.(event, {
				...(getItemIndexes(id) ?? {}),
				isAuxClick,
				resultType,
			}),
		[onResultClicked, getItemIndexes, id, resultType],
	);
	const onContextMenu = useCallback(
		(event: React.MouseEvent<HTMLDivElement> | React.MouseEvent<HTMLButtonElement>) =>
			onResultContextMenu?.(event, {
				...(getItemIndexes(id) ?? {}),
				resultType,
			}),
		[onResultContextMenu, getItemIndexes, id, resultType],
	);

	const metadataPartsWithTextOverflow = metadata.map((part, i) =>
		i === metadata.length - 1 && typeof part === 'string'
			? ([
					part,
					<Box xcss={textOverflowStyles} as="span">
						{part}
					</Box>,
				] as [string, ReactNode])
			: part,
	);

	const content = (
		<Box
			ref={(node: HTMLDivElement) => {
				ref.current = node;
				setRef(node);
			}}
			xcss={[
				resultStyles,
				isHovered && resultHoveredStlyes,
				isKeyboardHighlighted && keyboardHighlightedStyles,
			]}
			onMouseEnter={() => setIsHovered(true)}
			onMouseLeave={() => setIsHovered(false)}
		>
			<Box xcss={iconAreaStyles}>{icon}</Box>
			<Box xcss={middleAreaStyles}>
				<Box xcss={headingStyle}>
					<Box xcss={[titleStyles, textOverflowStyles]}>
						{highlightedTitle ? enrichResultTitle(highlightedTitle) : title}
					</Box>
					{titleMetadataParts && (
						<Box>
							<SearchMetadata parts={titleMetadataParts} />
						</Box>
					)}
				</Box>

				{metadata.length >= 1 && (
					<Box xcss={[metadataStyles]}>
						<SearchMetadata parts={metadataPartsWithTextOverflow} />
					</Box>
				)}
			</Box>
			{hoveredRightMetadata && isHovered ? (
				<Box xcss={rightSectionStyles}>
					<Show above="xs">{hoveredRightMetadata}</Show>
				</Box>
			) : highlightedRightMetadata && isKeyboardHighlighted ? (
				<Box xcss={rightSectionStyles}>
					<Show above="xs">{highlightedRightMetadata}</Show>
				</Box>
			) : rightMetadata ? (
				<Box xcss={rightSectionStyles}>
					<Show above="xs">{rightMetadata}</Show>
				</Box>
			) : null}
		</Box>
	);

	// There should not be any focusable descendant to an 'option' element
	const ariaProps = {
		role: 'option',
		id: navigationTrackingId || undefined,
		'aria-selected': isKeyboardHighlighted,
	};

	const resultWithoutAnalyticsWrapper = (
		<Box xcss={containerStyles}>
			{fg('custom-interactive-elements-not-keyboard-focusable') ? (
				<Pressable
					// If there is no url, the aria props sit here
					{...(!url && ariaProps)}
					// Pressable renders a native <button>, which only resets
					// box-sizing/appearance/border — NOT padding. Without this the
					// browser's default button padding is added on top of the row's
					// own padding, so the row inset differs from the <Box> (div)
					// variant. Reset it so both gate states render identically.
					padding="space.0"
					backgroundColor="color.background.neutral.subtle"
					onClick={(e) => onClick(e)}
					onAuxClick={(e) => {
						if (isAuxClick(e)) {
							return onClick(e, {
								isAuxClick: true,
							});
						}
					}}
					onContextMenu={onContextMenu}
				>
					{wrappedUrl ? (
						<Link
							{...ariaProps}
							xcss={[linkStyles]}
							rel={isThirdParty ? 'noreferrer' : ''}
							target={isThirdParty ? '_blank' : undefined}
							href={wrappedUrl}
							testId="search-dialog-result-link"
							{...crossAppLinkHandlers}
						>
							{content}
						</Link>
					) : (
						<>{content}</>
					)}
				</Pressable>
			) : (
				// eslint-disable-next-line @atlassian/a11y/interactive-element-not-keyboard-focusable
				<Box
					// If there is no url, the aria props sit here
					{...(!url && ariaProps)}
					onClick={(e: React.MouseEvent<HTMLDivElement>) => onClick(e)}
					onAuxClick={(e: React.MouseEvent<HTMLDivElement>) => {
						if (isAuxClick(e)) {
							return onClick(e, {
								isAuxClick: true,
							});
						}
					}}
					onContextMenu={onContextMenu}
				>
					{wrappedUrl ? (
						<Link
							{...ariaProps}
							xcss={linkStyles}
							rel={isThirdParty ? 'noreferrer' : ''}
							target={isThirdParty ? '_blank' : undefined}
							href={wrappedUrl}
							testId="search-dialog-result-link"
							{...crossAppLinkHandlers}
						>
							{content}
						</Link>
					) : (
						<>{content}</>
					)}
				</Box>
			)}
		</Box>
	);

	return includeInAnalytics ? (
		<SearchDialogResultAnalyticsWrapper
			data-testid="search-dialog-result"
			ref={wrapperRef}
			resultType={resultType}
			product={product}
			id={id}
			connectorType={connectorType}
			minutesSinceActivityTimestamp={minutesSinceActivityTimestamp}
			recentActivityEventType={recentActivityEventType}
			objectType={objectType}
			zeroQueryType={zeroQueryType}
		>
			{resultWithoutAnalyticsWrapper}
		</SearchDialogResultAnalyticsWrapper>
	) : (
		resultWithoutAnalyticsWrapper
	);
};
